<template>
  <section>
    <div class="desc">
      <p>{{ $t('promotion.redepositBonus.redepositBonusDesc.line1') }}</p>
      <p>{{ $t('promotion.redepositBonus.redepositBonusDesc.line2') }}</p>
      <p>{{ $t('promotion.redepositBonus.redepositBonusDesc.line3') }}</p>
    </div>
    <RedepositBonusSlogan class="redeposit-bonus-slogan" />
    <div class="divide-line"></div>
    <SelectAccount
      class="select-account"
      :label="this.$t('common.selectAccountToJoin')"
      :options="dropdownList"
      @change="onAccountChange"
      :value="selectedAccountId"
    />
    <div class="table-wrapper">
      <AccountInfoTable :tableData="participants" :campaignId="campaignID" @stateChange="getParticipantsInfo" />
    </div>
    <div class="tnc-wrapper">
      <p>
        <i18n path="promotion.redepositBonus.tnc">
          <template v-slot:link>
            <a
              href="https://www.vtmarketsnet.com/promotions/deposit-bonus/redeposit/"
              target="_blank"
              data-testid="tnc"
              >{{ $t('promotion.redepositBonus.link') }}</a
            >
          </template>
        </i18n>
      </p>
      <div class="btn-wrapper">
        <Button
          v-bind="isEligibleOptIn ? participateCampaignBtnConfig : terminateCampaignBtnConfig"
          @click="optIn"
          :disabled="!isEligibleOptIn"
          >{{ $t('common.participateInActivitie') }}</Button
        >
        <Button
          v-bind="isEligibleOptOut ? participateCampaignBtnConfig : terminateCampaignBtnConfig"
          @click="showPopOut = true"
          :disabled="!isEligibleOptOut"
          >{{ $t('common.activityTerminated') }}</Button
        >
      </div>
    </div>
    <PopOut
      :visible.sync="showPopOut"
      :message="$t('promotion.redepositBonus.dialog.optOut.desc')"
      :confirmText="$t('promotion.redepositBonus.dialog.optOut.confirm')"
      @confirm="optOut"
    ></PopOut>
    <PopOut
      :visible.sync="isBlacklisted"
      :message="$t('promotion.redepositBonus.dialog.blacklist.desc')"
      :confirmText="$t('promotion.redepositBonus.dialog.blacklist.confirm')"
      :hideCancel="true"
    ></PopOut>
    <PopOut
      :visible.sync="isCampaignEnd"
      :message="$t('promotion.redepositBonus.dialog.campaignEnd.desc', { days: expiredDays })"
      :confirmText="$t('promotion.redepositBonus.dialog.campaignEnd.confirm')"
      :hideCancel="true"
    ></PopOut>
  </section>
</template>

<script>
import moment from 'moment';
import RedepositBonusSlogan from './Slogan.vue';
import SelectAccount from './SelectAccount';
import AccountInfoTable from './AccountInfoTable';
import PopOut from './PopOut';
import Button from '@/components/interact/Button.vue';
import { HIGHLIGHT_COLOR } from '@/constants/style';
import { apiCampaignInfo, apiOptInAcc, apiOptOutAcc } from '@/resource';

const participateCampaignBtnConfig = {
  bgColor: HIGHLIGHT_COLOR,
  color: '#ffffff',
  borderRadius: '34px',
  padding: '15px 53px',
  fontSize: '21px',
  lineHeight: 'normal',
  fontWeight: 500
};
const terminateCampaignBtnConfig = {
  bgColor: '#d1d1d1',
  color: '#ffffff',
  borderRadius: '34px',
  padding: '15px 53px',
  fontSize: '21px',
  lineHeight: 'normal',
  fontWeight: 500
};

export default {
  name: 'CampaignConditions',
  components: {
    RedepositBonusSlogan,
    SelectAccount,
    AccountInfoTable,
    Button,
    PopOut
  },
  data() {
    return {
      participateCampaignBtnConfig,
      terminateCampaignBtnConfig,
      participants: [],
      showPopOut: false,
      isBlacklisted: false,
      isCampaignEnd: false,
      selectedAccount: null,
      selectedAccountId: null,
      uuid: this.$store.state.common.uuid,
      isEligibleOptIn: false,
      isEligibleOptOut: false,
      expiredDays: 0
    };
  },
  mounted() {
    this.getParticipantsInfo();
  },
  computed: {
    campaignID() {
      let campaign = this.$store.state.promotion.eligibleCampaigns.find(c => c.campaignType === 'RE_DEPOSIT_BONUS');
      return campaign ? campaign.id : null;
    },
    dropdownList() {
      return this.participants.map(f => {
        return { value: f.mt4Account, label: `${f.accountType} - ${f.mt4Account}` };
      });
    },
    userId() {
      return this.$store.state.common.uuid;
    }
  },
  watch: {
    selectedAccount(val) {
      switch (val.status) {
        case 'S':
        case 'OO':
        case null:
          this.isEligibleOptIn = true;
          this.isEligibleOptOut = false;
          break;
        case 'OI':
        case 'E':
        case 'WD':
        case 'CV':
        case 'PS':
          this.isEligibleOptIn = false;
          this.isEligibleOptOut = true;
          break;
        default:
          this.isEligibleOptIn = false;
          this.isEligibleOptOut = false;
      }
    }
  },
  methods: {
    async getParticipantsInfo() {
      await apiCampaignInfo(this.campaignID)
        .then(resp => {
          this.participants = resp.data.data;
          if (this.participants.filter(f => f.status === 'B').length > 0) this.isBlacklisted = true;
          if (this.participants.filter(f => f.status === 'BCE').length > 0 && this.participants[0].expiring) {
            let today = moment();
            let lastDay = moment(this.participants[0].expiredDate);
            this.expiredDays = lastDay.diff(today, 'days');
            this.isCampaignEnd = true;
          }
          if (this.selectedAccountId) this.onAccountChange(this.selectedAccount.mt4Account);
        })
        .catch(e => {
          console.log(e);
        });
    },
    onAccountChange(val) {
      this.selectedAccount = null;
      let index = this.participants.findIndex(f => f.mt4Account === val);
      this.selectedAccount = this.participants[index];
      this.selectedAccountId = this.participants[index].mt4Account;
      if (this.selectedAccount.status === 'B') this.isBlacklisted = true;
    },
    optIn() {
      let body = {
        campaignId: this.campaignID,
        userId: this.userId,
        accountMt4Dto: {
          mt4Account: this.selectedAccount.mt4Account,
          currency: this.selectedAccount.currency,
          datasourceId: this.selectedAccount.mt4DatasourceId
        }
      };
      apiOptInAcc(body)
        .then(resp => {
          if (resp.data.data) {
            this.getParticipantsInfo();
            this.$message({
              message: this.$t('common.field.successful'),
              type: 'success'
            });
          } else {
            this.$message({
              message: this.$t('common.field.tryLater'),
              type: 'error'
            });
          }
        })
        .catch(e => {
          this.$message({
            message: this.$t('common.field.failed'),
            type: 'error'
          });
        });
    },
    optOut() {
      let body = {
        campaignId: this.campaignID,
        userId: this.userId,
        accountMt4Dto: {
          mt4Account: this.selectedAccount.mt4Account,
          currency: this.selectedAccount.currency,
          datasourceId: this.selectedAccount.mt4DatasourceId
        }
      };
      apiOptOutAcc(body)
        .then(resp => {
          if (resp.data.data) {
            this.getParticipantsInfo();
            this.$message({
              message: this.$t('common.field.successful'),
              type: 'success'
            });
          }
        })
        .catch(e => {
          this.$message({
            message: this.$t('common.field.failed'),
            type: 'error'
          });
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.desc {
  color: $broker-black;
  margin-top: 80px;
  text-align: center;
  font-family: Oblivian, PingFangTC-SemiBold;

  p {
    font-weight: 600;
    font-size: 21px;
    line-height: 135%;
    margin-bottom: 5px;
  }
}
.redeposit-bonus-slogan {
  margin-top: 104px;
  margin-bottom: 80px;
}

.select-account {
  margin: 52px auto 0 auto;
  width: 50%;
}

.divide-line {
  height: 1px;
  width: 80%;
  border-bottom: solid 1px rgba(151, 151, 151, 0.58);
  margin: 0 auto;
}

.table-wrapper {
  width: 65%;
  margin: 0 auto;
  margin-top: 40px;
}

.tnc-wrapper {
  margin-top: 81px;
  font-size: 21px;
  @media (max-width: $DEVICE_SIZE_MOBILEL) {
    margin-top: 30px;
  }
}
.btn-wrapper {
  margin-top: 30px;
  @media (max-width: $DEVICE_SIZE_MOBILEL) {
    margin-top: 30px;
    display: flex;
    justify-content: center;
    gap: 20px;
  }
  .el-button {
    &:not(:last-child) {
      margin-right: 30px;
    }
  }
}
</style>
